'use client';

import { Box, HStack, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { shallowEqual, useSelector } from 'react-redux';
import { useActiveBrand } from '@/_hooks/useActiveBrand';
import { IRedux } from '@/_store/types';
import { Link } from '@/i18n/navigation';
import getShipperById from '@/_service/shipper.service';
import { BikeIcon, CircleQuestionMark } from 'lucide-react';

export default function ProductDeliveryInfo({
  isModal,
}: {
  isModal?: boolean;
}) {
  const t = useTranslations();
  const shipperId = useActiveBrand();
  const { branches } = useSelector(
    (state: IRedux) => ({ branches: state.common.branches }),
    shallowEqual,
  );
  const branch = shipperId ? branches?.[shipperId] : undefined;

  const { data: shipper } = useQuery({
    queryKey: ['shipper', shipperId],
    queryFn: () => getShipperById(shipperId).then((res) => res.data),
    enabled: Boolean(shipperId) && !branch?.delivery_time,
  });

  if (isModal) return null;

  const deliveryTime =
    branch?.delivery_time ||
    Number(shipper?.max_delivery_time) ||
    Number(shipper?.future_order_time) ||
    0;

  if (!deliveryTime) return null;

  return (
    <>
      <HStack
        gap={2}
        alignItems='center'
        borderRadius='lg'
        bg='background'
        w='fit-content'
        fontSize='xs'
        px={2}
        py={1}
      >
        <BikeIcon size={16} color='var(--primary-default)' />
        <Text fontWeight={500}>
          {t('deliver_in_minutes', { deliveryTime })}
        </Text>
      </HStack>
      <Link href='/delivery-terms'>
        <HStack
          gap={2}
          alignItems='center'
          borderRadius='lg'
          bg='background'
          w='fit-content'
          fontSize='xs'
          px={2}
          py={1}
          _hover={{
            textDecoration: 'underline',
          }}
        >
          <CircleQuestionMark size={16} color='var(--primary-default)' />
          <Text fontWeight={500}>{t('delivery_and_payment_terms')}</Text>
        </HStack>
      </Link>
    </>
  );
}
